import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { APP_NAME } from '@/lib/brand';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Home, Layers, Loader2, Search } from 'lucide-react';
import {
  INVENTORY_TABS, PROPERTY_TYPES, DEVELOPMENT_TYPES, adsPanelVisible,
} from './inventoryCatalog';

function TenantSelect({ companies, value, onChange, testid }) {
  return (
    <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
      <SelectTrigger className="bg-black/20 border-white/10 text-xs w-48" data-testid={testid}>
        <SelectValue placeholder="Tenant" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Todos los tenants</SelectItem>
        {(companies || []).map((c) => (
          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function InventoryPanel({ companies = [] }) {
  const [tab, setTab] = useState('properties');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [adSlots, setAdSlots] = useState([]);

  const load = async (opts = {}) => {
    const t = opts.tab ?? tab;
    const s = opts.search ?? search;
    const cid = opts.companyId ?? companyId;
    const st = opts.status ?? status;
    const k = opts.kind ?? kind;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (s) params.append('search', s);
      if (cid) params.append('company_id', cid);
      if (st) params.append('status', st);
      if (t === 'properties' && k) params.append('property_type', k);
      if (t === 'developments' && k) params.append('dev_type', k);
      params.append('limit', '50');
      const path = t === 'developments' ? '/admin/developments' : '/admin/properties';
      const r = await api.get(`${path}?${params.toString()}`);
      if (t === 'developments') {
        setItems(r.data.developments || []);
      } else {
        setItems(r.data.properties || []);
      }
      setTotal(r.data.total || 0);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tab]); // eslint-disable-line
  useEffect(() => {
    api.get('/admin/ad-slots').then((r) => setAdSlots(r.data || [])).catch(() => setAdSlots([]));
  }, []);

  const changeTab = (next) => {
    setTab(next);
    setKind('');
    setStatus('');
    setSearch('');
    setItems([]);
  };

  const applyFilters = (patch) => {
    if (patch.companyId !== undefined) setCompanyId(patch.companyId);
    if (patch.status !== undefined) setStatus(patch.status);
    if (patch.kind !== undefined) setKind(patch.kind);
    load({ ...patch });
  };

  const togglePortalFlag = async (propertyId, field, current) => {
    const other = field === 'is_featured_public' ? 'is_sponsored_public' : 'is_featured_public';
    const row = items.find((p) => p.id === propertyId);
    try {
      await api.post(`/admin/properties/${propertyId}/portal-flags`, {
        [field]: !current,
        [other]: row?.[other] || false,
      });
      load();
      toast.success('Visibilidad de portal actualizada');
    } catch {
      toast.error('No se pudo actualizar el flag del portal');
    }
  };

  const showAds = adsPanelVisible(adSlots);

  return (
    <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="admin-properties">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
              <Home className="w-5 h-5 text-teal-400" /> Inventario
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Solo se mira. No se editan fichas ni unidades. {total} registro{total === 1 ? '' : 's'}.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => load()} className="border-white/10 text-xs h-8" data-testid="refresh-props-btn">
            <Search className="w-3 h-3 mr-1" /> Buscar
          </Button>
        </div>

        <div className="inline-flex gap-1 p-1 rounded-lg bg-black/30 border border-white/10 mb-4" data-testid="inventory-tabs">
          {INVENTORY_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => changeTab(t.key)}
              className={`px-3 py-1.5 rounded-md text-[11px] font-medium ${tab === t.key ? 'bg-teal-500/20 text-teal-200' : 'text-white/50 hover:text-white'}`}
              data-testid={`inventory-tab-${t.key}`}
            >
              {t.key === 'developments' ? <Layers className="w-3 h-3 inline mr-1" /> : <Home className="w-3 h-3 inline mr-1" />}
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder={tab === 'developments' ? 'Buscar por nombre, ciudad…' : 'Buscar por titulo, ciudad, zona…'}
            className="bg-black/20 border-white/10 text-xs flex-1 min-w-[200px]"
            data-testid="props-search"
          />
          <TenantSelect companies={companies} value={companyId} onChange={(v) => applyFilters({ companyId: v })} testid="inventory-filter-tenant" />
          {tab === 'properties' ? (
            <>
              <Select value={status || 'all'} onValueChange={(v) => applyFilters({ status: v === 'all' ? '' : v })}>
                <SelectTrigger className="bg-black/20 border-white/10 text-xs w-36" data-testid="props-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="available">Disponible</SelectItem>
                  <SelectItem value="reserved">Reservada</SelectItem>
                  <SelectItem value="sold">Vendida</SelectItem>
                </SelectContent>
              </Select>
              <Select value={kind || 'all'} onValueChange={(v) => applyFilters({ kind: v === 'all' ? '' : v })}>
                <SelectTrigger className="bg-black/20 border-white/10 text-xs w-36" data-testid="props-filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {PROPERTY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          ) : (
            <>
              <Select value={status || 'all'} onValueChange={(v) => applyFilters({ status: v === 'all' ? '' : v })}>
                <SelectTrigger className="bg-black/20 border-white/10 text-xs w-36" data-testid="devs-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="inactive">Inactivo</SelectItem>
                </SelectContent>
              </Select>
              <Select value={kind || 'all'} onValueChange={(v) => applyFilters({ kind: v === 'all' ? '' : v })}>
                <SelectTrigger className="bg-black/20 border-white/10 text-xs w-36" data-testid="devs-filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {DEVELOPMENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-teal-400 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            {tab === 'developments' ? <Layers className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" /> : <Home className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />}
            <p className="text-sm text-muted-foreground">{tab === 'developments' ? 'No hay desarrollos que coincidan' : 'No hay propiedades que coincidan'}</p>
          </div>
        ) : tab === 'properties' ? (
          <div className="space-y-2" data-testid="inventory-properties-list">
            <div className="hidden lg:grid grid-cols-8 gap-3 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
              <span className="col-span-2">Propiedad</span>
              <span>Empresa</span>
              <span className="text-center">Tipo</span>
              <span className="text-center">Precio</span>
              <span className="text-center">Status</span>
              <span className="text-center">Ciudad</span>
              <span className="text-center">Portal</span>
            </div>
            {items.map((p) => (
              <div key={p.id} className="grid grid-cols-1 lg:grid-cols-8 gap-2 lg:gap-3 items-center px-4 py-3 rounded-lg bg-black/20 border border-white/5 hover:border-white/10 transition-colors" data-testid={`prop-${p.id}`}>
                <div className="col-span-1 lg:col-span-2 flex items-center gap-3">
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0">
                      <Home className="w-5 h-5 text-teal-400" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-white truncate">{p.title || 'Sin titulo'}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{p.address || ''}</p>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground truncate hidden lg:block">{p.company_name}</p>
                <p className="text-[10px] text-center text-muted-foreground hidden lg:block capitalize">{p.property_type || '-'}</p>
                <p className="text-xs text-center text-white font-medium hidden lg:block">${(p.price || 0).toLocaleString()}</p>
                <div className="text-center hidden lg:block">
                  <Badge className={`text-[9px] ${p.status === 'available' || p.status === 'disponible' ? 'bg-emerald-500/10 text-emerald-400' : p.status === 'sold' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'}`}>
                    {p.status || 'N/A'}
                  </Badge>
                </div>
                <p className="text-[10px] text-center text-muted-foreground hidden lg:block">{p.city || '-'}</p>
                <div className="hidden lg:flex justify-center gap-1.5">
                  <Button variant={p.is_featured_public ? 'default' : 'outline'} size="sm" className="h-7 text-[10px]" onClick={() => togglePortalFlag(p.id, 'is_featured_public', p.is_featured_public)}>Destacada</Button>
                  <Button variant={p.is_sponsored_public ? 'default' : 'outline'} size="sm" className="h-7 text-[10px]" onClick={() => togglePortalFlag(p.id, 'is_sponsored_public', p.is_sponsored_public)}>Patrocinada</Button>
                </div>
                <div className="lg:hidden flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground">
                  <span className="text-white font-medium">${(p.price || 0).toLocaleString()}</span>
                  <Badge className="text-[9px] bg-white/5">{p.property_type}</Badge>
                  <span>{p.company_name}</span>
                  <span>{p.city}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2" data-testid="inventory-developments-list">
            <div className="hidden lg:grid grid-cols-7 gap-3 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
              <span className="col-span-2">Desarrollo</span>
              <span>Empresa</span>
              <span className="text-center">Tipo</span>
              <span className="text-center">Precio base</span>
              <span className="text-center">Status</span>
              <span className="text-center">Ciudad</span>
            </div>
            {items.map((d) => (
              <div key={d.id} className="grid grid-cols-1 lg:grid-cols-7 gap-2 lg:gap-3 items-center px-4 py-3 rounded-lg bg-black/20 border border-white/5" data-testid={`dev-${d.id}`}>
                <div className="col-span-1 lg:col-span-2 flex items-center gap-3">
                  {d.cover_image_url ? (
                    <img src={d.cover_image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center shrink-0">
                      <Layers className="w-5 h-5 text-violet-400" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-white truncate">{d.name || 'Sin nombre'}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{d.address || ''}</p>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground truncate hidden lg:block">{d.company_name}</p>
                <p className="text-[10px] text-center text-muted-foreground hidden lg:block capitalize">{d.type || '-'}</p>
                <p className="text-xs text-center text-white font-medium hidden lg:block">${Number(d.base_price || 0).toLocaleString()}</p>
                <div className="text-center hidden lg:block">
                  <Badge className={`text-[9px] ${d.is_active === false ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                    {d.is_active === false ? 'Inactivo' : 'Activo'}
                  </Badge>
                </div>
                <p className="text-[10px] text-center text-muted-foreground hidden lg:block">{d.city || '-'}</p>
                <div className="lg:hidden flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground">
                  <span className="text-white font-medium">${Number(d.base_price || 0).toLocaleString()}</span>
                  <Badge className="text-[9px] bg-white/5">{d.type}</Badge>
                  <span>{d.company_name}</span>
                  <span>{d.city}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {showAds && (
          <div className="mt-8 pt-6 border-t border-white/5 space-y-2" data-testid="ad-slots-panel">
            <h4 className="font-heading text-base text-white">Espacios publicitarios</h4>
            <p className="text-xs text-muted-foreground">Hay {adSlots.length} slot{adSlots.length === 1 ? '' : 's'}. El alta nueva vive en Tracking Publicitario de {APP_NAME}.</p>
            {adSlots.map((slot) => (
              <div key={slot.id} className="p-3 rounded-lg bg-black/20 border border-white/5 text-xs text-white/70">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-white">{slot.name}</span>
                  <Badge className="text-[9px] bg-white/5">{slot.status}</Badge>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">{slot.city || '-'} · {slot.zone || '-'} · ${slot.price} · {slot.utm_campaign || 'sin utm'}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
